import { Listbox, Transition } from "@headlessui/react";
import { ArrowPathIcon, CheckIcon } from "@heroicons/react/24/solid";
import { Fragment, useMemo, useState } from "react";
import { ICustomer } from "../../../utils/types";
import { useCurrentUser } from "_graphql/cache/auth";
import CartBottomCard from "./cart-bottom-cart";
import CustomerCard from "./customer-card";
import SelectDeliveryCard from "./select-delivery-card";
import SelectPickupCard from "./select-pickup-card";
import ShippingMethod from "./shipping-method";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { addCustomerToCart, addResellerToCart } from "../../../redux/slice/cart-slice";
import useGetCustomers from "../hooks/useGetCustomers";
import { useMutation } from "@apollo/client";
import { createCustomer as createCustomerMutation } from "_graphql/mutation/order";
import { notification } from "antd";
import { useNavigate } from "react-location";
import { LocationGenerics } from "router/location";
// import _ from "lodash";

const SHIPPING_OPTIONS = [
  {
    name: "Hub Pickup",
    description: "Pickup your order at our Hub",
    value: "Pickup (Available for selected products only)",
  },
  {
    name: "Delivery",
    description: "Order will be delivered.",
    value: "delivery",
  },
] as const;

type SelectedCustomer = {
  customerId: string;
  name: string;
  phone: string;
  landmark?: string;
  customerLocation?: string;
  addressId?: string;
};

const emptyCustomerInput: ICustomer = {
  customerId: "",
  customerLocation: "",
  landmark: "",
  name: "",
  phone: "",
  email: "",
};

function getAvailableShippingOptions(cartProducts: Array<{ inHouse?: boolean | Boolean }>) {
  const hasInHouseProducts = cartProducts.some((item) => item?.inHouse);
  return hasInHouseProducts ? [...SHIPPING_OPTIONS] : SHIPPING_OPTIONS.slice(1);
}

const AddCustomer = () => {
  const dispatch = useAppDispatch();
  const { cart } = useAppSelector((state) => state.cart);
  const user = useCurrentUser();
  const navigate = useNavigate<LocationGenerics>();

  const [inputObj, setInputObj] = useState<ICustomer>(emptyCustomerInput);
  const [selected, setSelected] = useState<SelectedCustomer | undefined>(undefined);
  const [note, setNote] = useState("");
  const [shippingMethod, setShippingMethod] = useState<string>(SHIPPING_OPTIONS[1].name);
  const [isAddCustomer, setIsAddCustomer] = useState(false);

  const { data: customersData, loading: customersLoading, error: customersError } = useGetCustomers(
    user?.id || ""
  );
  const customers = customersData?.getCustomers ?? [];

  const [createCustomer, { loading: creating }] = useMutation(createCustomerMutation, {
    onError(error) {
      notification.error({
        message: "Error creating customer",
        description: error.message,
      });
    },
    refetchQueries: "active",
  });

  const deliveryMethod = useMemo(
    () => SHIPPING_OPTIONS.find((x) => x.name === shippingMethod)?.value,
    [shippingMethod]
  );

console.log("cart.delivery", cart.delivery);
console.log("cart.pickup", cart.pickup);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputObj((prev) => ({ ...prev, [name]: value }));
  };

  const onCustomerSelect = (value: any) => {
    if (!value) {
      setSelected(undefined);
      return;
    }
    setSelected({
      name: value.name,
      customerId: value.id,
      landmark: undefined,
      customerLocation: undefined,
      phone: value.phone,
      addressId: undefined,
    });
  };

  const selectedListCustomer = customers.find((c: any) => c?.id === selected?.customerId) ?? null;
  const customerAddresses = selectedListCustomer?.address ?? [];
  const selectedAddress = customerAddresses.find((a: any) => a?.id === selected?.addressId) ?? null;

  const next = () => {
    const customerObj: ICustomer =
      selected !== undefined
        ? {
            customerId: selected.customerId,
            name: selected.name,
            phone: selected.phone,
            address_id: selected.addressId,
            customerLocation: selected.customerLocation ?? "",
          }
        : inputObj;

    const reseller = {
      referrer: user?.id ?? "",
      resellerName: user?.name ?? "",
      resellerPhone: user?.phone ?? "",
      username: user?.generic_name ?? "",
    };

    dispatch(addResellerToCart(reseller));

    if (isAddCustomer && selected === undefined) {
      // create customer
      createCustomer({
        variables: {
          input: {
            name: inputObj.name,
            phone: inputObj.phone,
            email: inputObj.email,
            address: {
              area: inputObj.customerLocation,
              city: cart.delivery?.location,
              landmark: inputObj.landmark,
            },
          },
        },
      }).then((result: any) => {
        if (result.data?.createCustomer) {
          customerObj.customerId = result.data.createCustomer.id;
          customerObj.address_id = result.data.createCustomer.address?.[0]?.id;
          dispatch(addCustomerToCart(customerObj));
          navigate({ to: "/cart/checkout" });
        }
      });
    } else {
      dispatch(addCustomerToCart(customerObj));
      navigate({ to: "/cart/checkout" });
    }
  };

  const nextDisabled =
    (deliveryMethod === "delivery" && !cart?.delivery) ||
    (deliveryMethod !== "delivery" && !cart?.pickup) ||
    creating ||
    Boolean(selected && !selected?.addressId);

  return (
    <div className="flex flex-col lg:mb-12 lg:max-w-7xl lg:mx-auto lg:px-4">
      <div className="flex flex-col rounded-sm bg-white p-2">
        <h3 className="text-gray-700 font-medium mb-2 text-base">Your Customer</h3>

        {!isAddCustomer && (
          <div className="pb-4">
            <Listbox value={selectedListCustomer} onChange={onCustomerSelect}>
              {({ open }) => (
                <div className="relative w-full">
                  <Listbox.Button className="flex items-center justify-between w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer focus:outline-none">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="h-8 w-8 bg-blue-100 flex items-center justify-center rounded-full shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-blue-700"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.25a8.25 8.25 0 1115 0v.75H4.5v-.75z"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <p className="text-gray-800 text-sm font-medium">
                          {customersLoading ? "Loading customers..." : "Select your customer"}
                        </p>
                        <p className="text-blackText text-xs truncate">{selected?.name}</p>
                      </div>
                    </div>
                    <span className="shrink-0 text-gray-700 text-sm font-medium bg-gray-100 px-3 py-1.5 rounded-lg">
                      Choose
                    </span>
                  </Listbox.Button>

                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-2 w-full bg-white shadow-lg max-h-60 rounded-xl py-2 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      {customers.length > 0 ? (
                        customers
                          .filter((c: any) => c != null)
                          .map((customer: any) => (
                            <Listbox.Option
                              key={customer.id}
                              className={({ active }) =>
                                `${active ? "bg-blue-100 text-blue-900" : "text-gray-900"} cursor-default select-none relative py-2 pl-3 pr-9`
                              }
                              value={customer}
                            >
                              {({ selected: isSelected, active }) => (
                                <div className="flex flex-col">
                                  <span className={`truncate ${isSelected ? "font-semibold" : "font-normal"}`}>
                                    {customer.name}
                                  </span>
                                  <span className={`truncate text-xs ${active ? "text-blue-700" : "text-gray-500"}`}>
                                    {customer.phone}
                                  </span>
                                  {isSelected && (
                                    <span
                                      className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                        active ? "text-blue-700" : "text-blue-600"
                                      }`}
                                    >
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </div>
                              )}
                            </Listbox.Option>
                          ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          {customersLoading ? "Loading..." : "No customers found"}
                        </div>
                      )}
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>

            {customersError && (
              <span className="mt-1 text-red-600 text-xs">Error loading customers</span>
            )}
          </div>
        )}

        {/* Address picker (after customer is chosen from the list) */}
        {selected && !isAddCustomer && (
          <Listbox
            value={selectedAddress}
            onChange={(addr: any) =>
              setSelected((prev) =>
                prev && addr
                  ? { ...prev, addressId: addr.id, customerLocation: addr.area }
                  : prev
              )
            }
          >
            {({ open }) => (
              <div className="relative w-full mt-4">
                <Listbox.Button className="flex items-center justify-between w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer focus:outline-none">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="h-8 w-8 bg-blue-100 flex items-center justify-center rounded-full shrink-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5 text-blue-700"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 2.25c3.728 0 6.75 3.022 6.75 6.75 0 4.303-6.75 12.75-6.75 12.75S5.25 13.303 5.25 9c0-3.728 3.022-6.75 6.75-6.75z"
                        />
                        <circle cx="12" cy="9" r="1.5" />
                      </svg>
                    </div>
                    <div className="min-w-0 text-left">
                      <p className="text-gray-800 text-sm font-medium">Select delivery address</p>
                      <p className="text-gray-400 text-xs truncate">
                        {selected?.customerLocation ?? "Choose a saved customer address"}
                      </p>
                    </div>
                  </div>
                  <span className="shrink-0 text-gray-700 text-sm font-medium bg-gray-100 px-3 py-1.5 rounded-lg">
                    Choose
                  </span>
                </Listbox.Button>

                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-2 w-full bg-white shadow-lg max-h-60 rounded-xl py-2 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {customerAddresses.length > 0 ? (
                      customerAddresses
                        .filter((a: any) => a != null)
                        .map((address: any) => (
                          <Listbox.Option
                            key={address.id}
                            value={address}
                            className={({ active }) =>
                              `${active ? "bg-blue-100 text-blue-900" : "text-gray-900"} cursor-default select-none relative py-2 pl-3 pr-9`
                            }
                          >
                            {({ selected: isSelected, active }) => (
                              <div className="flex flex-col">
                                <span className={`truncate text-sm ${isSelected ? "font-semibold" : "font-normal"}`}>
                                  {address.area}
                                </span>
                                {isSelected && (
                                  <span
                                    className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                      active ? "text-blue-700" : "text-blue-600"
                                    }`}
                                  >
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                )}
                              </div>
                            )}
                          </Listbox.Option>
                        ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500 text-sm">No addresses found</div>
                    )}
                  </Listbox.Options>
                </Transition>
              </div>
            )}
          </Listbox>
        )}

        {/* New customer form */}
        {isAddCustomer && selected === undefined && (
          <>
            <span className="text-gray-700 mb-1 text-sm">Add new customer</span>
            <div className="space-y-2 pb-4">
              <div>
                <label htmlFor="customerName" className="block text-sm font-medium text-gray-500">
                  Customer name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="name"
                    id="customerName"
                    className="shadow-sm focus:ring-tendo-active focus:border-tendo-active block w-full sm:text-sm border-gray-300 rounded-md px-4 py-2 border"
                    placeholder="Enter your customer name"
                    value={inputObj.name}
                    onChange={onInputChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="customerPhone" className="block text-sm font-medium text-gray-500">
                  Customer phone number
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="phone"
                    id="customerPhone"
                    className="shadow-sm focus:ring-tendo-active focus:border-tendo-active block w-full sm:text-sm border-gray-300 rounded-md px-4 py-2 border"
                    placeholder="Enter your customer phone number"
                    value={inputObj.phone}
                    onChange={onInputChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-500">
                  Customer email (optional)
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    name="email"
                    id="customerEmail"
                    className="shadow-sm focus:ring-tendo-active focus:border-tendo-active block w-full sm:text-sm border-gray-300 rounded-md px-4 py-2 border"
                    placeholder="Enter your customer email (optional)"
                    value={inputObj.email ?? ""}
                    onChange={onInputChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="customerLocation" className="block text-sm font-medium text-gray-500">
                  Customer location
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="customerLocation"
                    id="customerLocation"
                    className="shadow-sm focus:ring-tendo-active focus:border-tendo-active block w-full sm:text-sm border-gray-300 rounded-md px-4 py-2 border"
                    placeholder="Enter your customer location"
                    value={inputObj.customerLocation}
                    onChange={onInputChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="customerLandmark" className="block text-sm font-medium text-gray-500">
                  Landmark
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="landmark"
                    id="customerLandmark"
                    className="shadow-sm focus:ring-tendo-active focus:border-tendo-active block w-full sm:text-sm border-gray-300 rounded-md px-4 py-2 border"
                    placeholder="Enter landmark close to the location"
                    value={inputObj.landmark ?? ""}
                    onChange={onInputChange}
                  />
                </div>
              </div>
            </div>

            <span className="text-gray-500 mt-4 mb-2 text-xs">
              New created customer will be saved automatically
            </span>
          </>
        )}

        {/* Toggle between list selection and "add new" form */}
        <div className="flex items-center justify-end w-full">
          {!isAddCustomer ? (
            <button
              type="button"
              className="text-blue-500 mt-4 mb-2 text-sm bg-transparent border-none cursor-pointer p-0"
              onClick={() => {
                setIsAddCustomer(true);
                setSelected(undefined);
              }}
            >
              Add new customer
            </button>
          ) : (
            <button
              type="button"
              className="text-blue-500 mt-4 mb-2 text-sm bg-transparent border-none cursor-pointer p-0"
              onClick={() => setIsAddCustomer(false)}
            >
              Select from customers
            </button>
          )}
        </div>
      </div>

      {/* Shipping method */}
      <div className="py-4">
        <ShippingMethod
          data={getAvailableShippingOptions(cart.products as any[])}
          value={shippingMethod}
          setValue={setShippingMethod}
        />
      </div>

      {/* Delivery or Pickup zone cards */}
      {deliveryMethod === "delivery" ? (
        <SelectDeliveryCard setNote={setNote} note={note} />
      ) : (
        <SelectPickupCard />
      )}

      {/* Customer summary card */}
      {selected && <CustomerCard customer={selected} clearCustomer={() => setSelected(undefined)} />}

      <CartBottomCard
        cart={{
          products: cart.products as any[],
          totalResellerProfit: cart.totalResellerProfit,
          delivery: cart.delivery ? { price: cart.delivery.price } : undefined,
        }}
      />

      <div className="flex my-3 w-full">
        <button
          type="button"
          className="w-full flex justify-center items-center py-3 px-4 text-base font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:bg-gray-100 disabled:text-blue-500 disabled:cursor-not-allowed bg-primary-600 text-white"
          disabled={nextDisabled}
          onClick={next}
        >
          {creating ? (
            <>
              <ArrowPathIcon className="h-5 w-5 animate-spin text-white mr-2" aria-hidden />
              Loading ...
            </>
          ) : (
            "Next"
          )}
        </button>
      </div>
    </div>
  );
};

export default AddCustomer;
